import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, CheckCircle2Icon, LockIcon, MailIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { describeSignInTarget, isBuildEnvironment, sendSignInLink, signInWithPassword, startSetupSession, type StudioSession } from '../data/teamAccess';
interface StudioLoginProps {
  onSignedIn: (session: StudioSession) => void;
}

/**
 * Password sign-in leads because Supabase's built-in email sender is capped at a
 * couple of messages an hour, which makes link sign-in unreliable until an email
 * sender is connected. Both paths create the same real Supabase session.
 */
export function StudioLogin({
  onSignedIn
}: StudioLoginProps) {
  const [mode, setMode] = useState<'password' | 'link'>('password');
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [sentTo, setSentTo] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    if (mode === 'password') {
      const failure = await signInWithPassword(identifier, password);
      setBusy(false);
      if (failure) setError(failure);
      // Success is picked up by the session listener in the app shell.
      return;
    }
    const failure = await sendSignInLink(identifier);
    setBusy(false);
    if (failure) {
      setError(failure);
      return;
    }
    setSentTo(describeSignInTarget(identifier));
  };
  const switchMode = (next: 'password' | 'link') => {
    setMode(next);
    setError('');
    setPassword('');
  };
  return <main className="flex min-h-screen w-full items-center justify-center bg-ink-900 px-5 py-12" data-id="element-1808" data-mp="1p1h6r86ngn2v">
      <div className="w-full max-w-sm" data-id="element-1809" data-mp="hjq87hfce64e">
        <div className="mb-7 text-center" data-id="element-1810" data-mp="djzjdj9dm02i">
          <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-xl bg-accent shadow-glow" data-id="element-1811" data-mp="dixw8gf35n4f">
            <LockIcon className="h-5 w-5 text-white" data-id="element-1812" data-mp="ezk2b47645l7" />
          </div>
          <h1 className="mt-4 text-[24px] font-semibold tracking-tight text-white" data-id="element-1813" data-mp="jzzdbp3z9100">Band login</h1>
          <p className="mt-1.5 text-[13px] leading-relaxed text-zinc-500" data-id="element-1814" data-mp="52p4yommsb8i">
            {sentTo ? 'Open the email and tap the link to get in.' : mode === 'password' ? 'Sign in with the email on your access list and your password.' : 'Enter the email on your access list and we will send you a sign-in link.'}
          </p>
        </div>

        {sentTo ? <div className="space-y-3" data-id="element-1815" data-mp="3ijy46ovo3h4s">
            <div className="rounded-xl border border-emerald-400/20 bg-emerald-400/[0.07] p-4 text-center" data-id="element-1816" data-mp="3ngybf3496pxh">
              <CheckCircle2Icon className="mx-auto h-5 w-5 text-emerald-300" data-id="element-1817" data-mp="39gx4p71b4oa3" />
              <p className="mt-2 text-[13px] text-white" data-id="element-1818" data-mp="3dj0nkgqb2v5e">Link sent to {sentTo}</p>
              <p className="mt-1.5 text-[11.5px] leading-relaxed text-zinc-400" data-id="element-1819" data-mp="3dkv1vc72np0i">
                It arrives within a minute and opens the studio straight away. Check your spam folder if it does not
                show up.
              </p>
            </div>
            <button type="button" onClick={() => {
          setSentTo('');
          setError('');
        }} className="w-full text-[12px] font-medium text-zinc-500 transition-colors duration-150 ease-out hover:text-white" data-id="element-1820" data-mp="33w7m7xi2lf6n">
              Use a different email
            </button>
          </div> : <form onSubmit={submit} className="space-y-3" data-id="element-1821" data-mp="3mm5twchyh69r">
            <label className="block" data-id="element-1822" data-mp="3mnqmvxomgh5i">
              <span className="mb-1.5 block text-[12px] font-medium text-zinc-400" data-id="element-1823" data-mp="3ik2g3h7tdplb">Email address</span>
              <input value={identifier} onChange={event => setIdentifier(event.target.value)} type="email" autoComplete="username" autoFocus placeholder="you@example.com" className="w-full rounded-xl border border-white/[0.1] bg-ink-800 px-3 py-2.5 text-[13px] text-white outline-none placeholder:text-zinc-600 focus:border-[#d97742]/70" data-id="element-1824" data-mp="fr32xc8kt6f" />
            </label>

            {mode === 'password' && <label className="block" data-id="element-1825" data-mp="t8m43xdcqu34">
                <span className="mb-1.5 block text-[12px] font-medium text-zinc-400" data-id="element-1826" data-mp="3soitm3xtmuly">Password</span>
                <input value={password} onChange={event => setPassword(event.target.value)} type="password" autoComplete="current-password" placeholder="••••••••" className="w-full rounded-xl border border-white/[0.1] bg-ink-800 px-3 py-2.5 text-[13px] text-white outline-none placeholder:text-zinc-600 focus:border-[#d97742]/70" data-id="element-1827" data-mp="k6tro560zy4" />
              </label>}

            {error && <div role="alert" className="rounded-xl border border-rose-400/20 bg-rose-400/[0.07] p-3" data-id="element-1828" data-mp="36g3mkr64ldr4">
                <p className="text-[12px] leading-relaxed text-rose-200" data-id="element-1829" data-mp="31ghcurpiafn2">{error}</p>
              </div>}

            <Button type="submit" disabled={busy} className="w-full justify-center" data-id="element-1830" data-mp="2woprqwp4d24b">
              {mode === 'password' ? <>
                  <LockIcon className="h-4 w-4" data-id="element-1831" data-mp="2npcj268tkhkn" /> {busy ? 'Signing in…' : 'Sign in'}
                </> : <>
                  <MailIcon className="h-4 w-4" data-id="element-1832" data-mp="2ipgd41gkag2g" /> {busy ? 'Sending…' : 'Email me a sign-in link'}
                </>}
            </Button>

            <button type="button" onClick={() => switchMode(mode === 'password' ? 'link' : 'password')} className="w-full text-[12px] font-medium text-zinc-500 transition-colors duration-150 ease-out hover:text-white" data-id="element-1833" data-mp="28s3ci5ay378c">
              {mode === 'password' ? 'Email me a sign-in link instead' : 'Use a password instead'}
            </button>
          </form>}

        {isBuildEnvironment() && <div className="mt-6 rounded-xl border border-white/[0.07] bg-ink-850 p-3 text-center" data-id="element-1834" data-mp="2698bxwbv6mid">
            <p className="text-[11.5px] leading-relaxed text-zinc-500" data-id="element-1835" data-mp="1rkc522ljnfjc">
              Building the site? Skip sign-in. This shortcut only appears while building and never on the published
              site — but it cannot save to the cloud, so sign in properly before editing real content.
            </p>
            <button type="button" onClick={() => onSignedIn(startSetupSession())} className="mt-2 text-[12px] font-medium text-accent-soft transition-colors duration-150 ease-out hover:text-white" data-id="element-1836" data-mp="2p5qbgzwyfqp6">
              Continue without signing in
            </button>
          </div>}

        <Link to="/" className="mt-5 flex items-center justify-center gap-1.5 text-[12px] font-medium text-zinc-500 transition-colors duration-150 ease-out hover:text-white" data-id="element-1837" data-mp="13sx0022duk4e">
          <ArrowLeftIcon className="h-3.5 w-3.5" data-id="element-1838" data-mp="rat5v1ccmmsl" /> Back to the band site
        </Link>
      </div>
    </main>;
}